import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import { auth, db } from "../config/firebaseConfig";
import {
  normalizeStudentUid,
  shouldActivateQrForStudentName,
} from "../utils/studentQr";
import {
  getAllowedRoles,
  hasPermission,
  PERMISSIONS,
} from "../utils/roleAccess";

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

const TEMPLATE_HEADERS = [
  "uid",
  "name",
  "className",
  "rollNo",
  "phone",
  "altPhone",
  "address",
  "qrImageUrl",
  "activeqr",
  "studentImageUrl",
  "father",
  "fatherAadhaar",
  "fatherImageUrl",
  "mother",
  "motherAadhaar",
  "motherImageUrl",
  "guardianName",
  "guardianRelation",
  "guardianPhone",
  "guardianAddress",
  "guardianImageUrl",
  "dob",
  "admissionDate",
  "studentAadhaar",
];

// Fields that can never be unchecked (used as match key or system fields)
const ALWAYS_LOCKED = new Set(["uid", "updatedAt"]);

// Field groups for the checkbox selector UI
const FIELD_GROUPS = [
  {
    label: "Identity",
    fields: [
      "uid",
      "name",
      "className",
      "rollNo",
      "dob",
      "admissionDate",
      "studentAadhaar",
    ],
  },
  {
    label: "Contact",
    fields: ["phone", "altPhone", "address"],
  },
  {
    label: "QR & Images",
    fields: ["qrImageUrl", "activeqr", "studentImageUrl"],
  },
  {
    label: "Father",
    fields: ["father", "fatherAadhaar", "fatherImageUrl"],
  },
  {
    label: "Mother",
    fields: ["mother", "motherAadhaar", "motherImageUrl"],
  },
  {
    label: "Guardian",
    fields: [
      "guardianName",
      "guardianRelation",
      "guardianPhone",
      "guardianAddress",
      "guardianImageUrl",
    ],
  },
];

// All updatable fields (excludes always-locked ones for default-all-checked init)
const ALL_UPDATABLE_FIELDS = FIELD_GROUPS.flatMap((g) => g.fields).filter(
  (f) => !ALWAYS_LOCKED.has(f),
);

const NUMERIC_TO_STRING_FIELDS = new Set([
  "rollNo",
  "phone",
  "altPhone",
  "fatherAadhaar",
  "motherAadhaar",
  "studentAadhaar",
  "guardianPhone",
]);

const CLASS_OPTIONS = [
  "Nursery",
  "Nursery_A",
  "Nursery_B",
  "LKG",
  "UKG",
  "Class 1",
  "Class 2",
  "Class 3",
  "Class 4",
  "Class 5",
  "Class 6",
  "Class 7",
  "Class 8",
];

const NORMALIZE_MAP = {
  nc: "Nursery",
  nur: "Nursery",
  nursery: "Nursery",
  nurseries: "Nursery",
  nurserya: "Nursery_A",
  nurseryb: "Nursery_B",
  "l.k.g": "LKG",
  lkg: "LKG",
  "l k g": "LKG",
  i: "Class 1",
  ii: "Class 2",
  iii: "Class 3",
  iv: "Class 4",
  v: "Class 5",
  vi: "Class 6",
  vii: "Class 7",
};

// Max records to show in rollback history UI
const MAX_ROLLBACK_HISTORY = 10;

// Firestore batch write limit
const BATCH_SIZE_LIMIT = 400;

// ─────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────

function normalizeClassName(raw) {
  if (!raw && raw !== 0) return "";
  const s = String(raw).trim();
  if (!s) return "";
  const key = s.toLowerCase().replace(/\./g, "").replace(/\s+/g, "");
  if (NORMALIZE_MAP[key]) return NORMALIZE_MAP[key];
  for (const opt of CLASS_OPTIONS) {
    if (opt.toLowerCase() === s.toLowerCase()) return opt;
  }
  return s;
}

function normalizeDateToYmd(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeExcelValue(fieldKey, value) {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return normalizeDateToYmd(value);
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    return NUMERIC_TO_STRING_FIELDS.has(fieldKey) ? String(value) : value;
  }
  if (typeof value === "string") return value.trim();
  return String(value);
}

function normalizeRow(rawRow = {}) {
  const normalized = {};
  TEMPLATE_HEADERS.forEach((fieldKey) => {
    normalized[fieldKey] = normalizeExcelValue(fieldKey, rawRow[fieldKey]);
  });
  Object.entries(rawRow).forEach(([key, value]) => {
    if (key in normalized) return;
    normalized[key] = normalizeExcelValue(key, value);
  });
  return normalized;
}

function isBlankRow(row) {
  if (!row) return true;
  return Object.values(row).every((value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim() === "";
    return false;
  });
}

function getReadableErrorMessage(error) {
  return error?.message || "Unexpected error. Please try again.";
}

function buildPreviewValue(value) {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (
    value &&
    typeof value === "object" &&
    typeof value.toDate === "function"
  ) {
    return normalizeDateToYmd(value.toDate());
  }
  return String(value);
}

function buildExportRow(student = {}, extraHeaders) {
  const row = {};
  [...TEMPLATE_HEADERS, ...extraHeaders].forEach((key) => {
    const value = student[key];
    if (
      value &&
      typeof value === "object" &&
      typeof value.toDate === "function"
    ) {
      row[key] = normalizeDateToYmd(value.toDate());
      return;
    }
    row[key] = normalizeExcelValue(key, value);
  });
  return row;
}

// ─────────────────────────────────────────────
// Image export helpers
// ─────────────────────────────────────────────

const IMAGE_EXPORT_COLUMNS = new Set([
  "qrImageUrl",
  "studentImageUrl",
  "fatherImageUrl",
  "motherImageUrl",
  "guardianImageUrl",
]);

const IMAGE_CELL_SIZE = { width: 84, height: 84 };

function inferImageExtension(contentType = "", fallbackUrl = "") {
  const t = String(contentType || "").toLowerCase();
  if (t.includes("png")) return "png";
  if (t.includes("webp")) return "webp";
  if (t.includes("gif")) return "gif";
  if (t.includes("bmp")) return "bmp";
  if (t.includes("tiff")) return "tiff";
  if (t.includes("heic")) return "heic";
  if (t.includes("heif")) return "heif";
  const m = String(fallbackUrl || "").match(/\.([a-z0-9]{2,6})(?:\?|#|$)/i);
  return m ? m[1].toLowerCase() : "jpeg";
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Unable to read image file."));
    reader.readAsDataURL(blob);
  });
}

async function fetchImageDataForExcel(imageUrl) {
  const url = String(imageUrl || "").trim();
  if (!url) return null;
  try {
    const response = await fetch(url, { method: "GET" });
    if (!response.ok) return null;
    const blob = await response.blob();
    const base64 = await blobToDataUrl(blob);
    return { base64, extension: inferImageExtension(blob.type || "", url) };
  } catch {
    return null;
  }
}

async function buildActiveQrWorkbook(
  students,
  extraHeaders,
  embedImages = true,
) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Rising Stars Public School";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet("Active QR Students");
  const headers = [...TEMPLATE_HEADERS, ...extraHeaders];

  worksheet.columns = headers.map((header) => ({
    header,
    key: header,
    width: IMAGE_EXPORT_COLUMNS.has(header) ? 16 : 18,
  }));

  worksheet.getRow(1).font = { bold: true };
  worksheet.getRow(1).alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };

  for (const student of students) {
    const rowValues = {};
    for (const key of headers) {
      // If embedding images is enabled and this is an image column, leave cell empty
      // so image gets placed; otherwise export the URL/text.
      if (IMAGE_EXPORT_COLUMNS.has(key) && embedImages) {
        rowValues[key] = "";
      } else {
        rowValues[key] = buildPreviewValue(student[key]);
      }
    }

    const row = worksheet.addRow(rowValues);
    row.height = 68;
    row.alignment = { vertical: "middle", wrapText: true };

    if (embedImages) {
      for (const key of IMAGE_EXPORT_COLUMNS) {
        const imageUrl = String(student[key] || "").trim();
        if (!imageUrl) continue;
        const imageData = await fetchImageDataForExcel(imageUrl);
        if (!imageData) continue;
        const imageId = workbook.addImage({
          base64: imageData.base64,
          extension: imageData.extension,
        });
        const columnIndex = headers.indexOf(key);
        if (columnIndex === -1) continue;
        worksheet.addImage(imageId, {
          tl: { col: columnIndex, row: row.number - 1 },
          ext: IMAGE_CELL_SIZE,
        });
      }
    }
  }

  return workbook;
}

// ─────────────────────────────────────────────
// Rollback helpers
// ─────────────────────────────────────────────

/**
 * Saves a rollback snapshot to localStorage.
 * Each entry: { id, timestamp, label, count, data[] }
 */
function saveRollbackSnapshot(label, data) {
  try {
    const existing = JSON.parse(
      localStorage.getItem("studentRollbackHistory") || "[]",
    );
    const entry = {
      id: `rb_${Date.now()}`,
      timestamp: new Date().toISOString(),
      label,
      count: data.length,
      data,
    };
    // Keep only the most recent MAX_ROLLBACK_HISTORY snapshots
    const updated = [entry, ...existing].slice(0, MAX_ROLLBACK_HISTORY);
    localStorage.setItem("studentRollbackHistory", JSON.stringify(updated));
    return entry.id;
  } catch {
    // localStorage might be full; fail silently
    return null;
  }
}

function getRollbackHistory() {
  try {
    return JSON.parse(localStorage.getItem("studentRollbackHistory") || "[]");
  } catch {
    return [];
  }
}

function deleteRollbackSnapshot(id) {
  try {
    const existing = getRollbackHistory().filter((e) => e.id !== id);
    localStorage.setItem("studentRollbackHistory", JSON.stringify(existing));
  } catch {
    // ignore
  }
}

function downloadRollbackAsExcel(snapshot) {
  const rows = (snapshot.data || []).map((d) => buildExportRow(d, []));
  const sheet = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, "Rollback_Backup");
  const ts = snapshot.timestamp.replace(/[:.]/g, "-").slice(0, 19);
  XLSX.writeFile(wb, `rollback_backup_${ts}.xlsx`);
}

// ─────────────────────────────────────────────
// Sub-component: FieldSelector
// ─────────────────────────────────────────────

function FieldSelector({ selectedFields, onChange }) {
  const handleToggleField = (field) => {
    if (ALWAYS_LOCKED.has(field)) return;
    const next = new Set(selectedFields);
    if (next.has(field)) next.delete(field);
    else next.add(field);
    onChange(next);
  };

  const handleGroupSelectAll = (fields) => {
    const updatable = fields.filter((f) => !ALWAYS_LOCKED.has(f));
    const allChecked = updatable.every((f) => selectedFields.has(f));
    const next = new Set(selectedFields);
    updatable.forEach((f) => (allChecked ? next.delete(f) : next.add(f)));
    onChange(next);
  };

  const handleSelectAll = () => {
    onChange(new Set(ALL_UPDATABLE_FIELDS));
  };

  const handleDeselectAll = () => {
    onChange(new Set());
  };

  const checkedCount = ALL_UPDATABLE_FIELDS.filter((f) =>
    selectedFields.has(f),
  ).length;

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Stack spacing={1.5}>
          {/* Header row */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2" fontWeight={700}>
                Fields to Update
              </Typography>
              <Chip
                label={`${checkedCount} / ${ALL_UPDATABLE_FIELDS.length} selected`}
                size="small"
                color={
                  checkedCount === 0
                    ? "error"
                    : checkedCount === ALL_UPDATABLE_FIELDS.length
                      ? "success"
                      : "primary"
                }
                variant="outlined"
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                sx={{ fontSize: 11, borderRadius: 999 }}
                onClick={handleSelectAll}
              >
                Select all
              </Button>
              <Button
                size="small"
                sx={{ fontSize: 11, borderRadius: 999 }}
                color="inherit"
                onClick={handleDeselectAll}
              >
                Deselect all
              </Button>
            </Stack>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Only checked fields will be written to Firestore.{" "}
            <strong>uid</strong> is always used for matching and cannot be
            unchecked.
          </Typography>

          <Divider />

          {/* Groups */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr",
              },
              gap: 2,
            }}
          >
            {FIELD_GROUPS.map((group) => {
              const updatable = group.fields.filter(
                (f) => !ALWAYS_LOCKED.has(f),
              );
              const allChecked =
                updatable.length > 0 &&
                updatable.every((f) => selectedFields.has(f));
              const someChecked = updatable.some((f) => selectedFields.has(f));

              return (
                <Box key={group.label}>
                  {/* Group header checkbox */}
                  <FormControlLabel
                    sx={{ mb: 0.5 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={allChecked}
                        indeterminate={someChecked && !allChecked}
                        onChange={() => handleGroupSelectAll(group.fields)}
                        disabled={updatable.length === 0}
                      />
                    }
                    label={
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="text.secondary"
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        {group.label}
                      </Typography>
                    }
                  />
                  <FormGroup sx={{ pl: 1 }}>
                    {group.fields.map((field) => {
                      const locked = ALWAYS_LOCKED.has(field);
                      return (
                        <FormControlLabel
                          key={field}
                          sx={{ height: 28 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={locked || selectedFields.has(field)}
                              disabled={locked}
                              onChange={() => handleToggleField(field)}
                            />
                          }
                          label={
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontFamily: "monospace" }}
                              >
                                {field}
                              </Typography>
                              {locked && (
                                <Chip
                                  label="locked"
                                  size="small"
                                  sx={{ height: 14, fontSize: 9, px: 0.3 }}
                                />
                              )}
                            </Stack>
                          }
                        />
                      );
                    })}
                  </FormGroup>
                </Box>
              );
            })}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────

// ─────────────────────────────────────────────

function RollbackPanel({ onRollbackComplete, setSnackbar }) {
  const [history, setHistory] = useState([]);
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [rolling, setRolling] = useState(false);

  useEffect(() => {
    setHistory(getRollbackHistory());
  }, []);

  const refresh = () => setHistory(getRollbackHistory());

  const handleDelete = (id) => {
    deleteRollbackSnapshot(id);
    refresh();
  };

  const handleRollback = useCallback(async () => {
    if (!rollbackTarget) return;
    setRolling(true);
    try {
      const rows = rollbackTarget.data || [];
      if (!rows.length) throw new Error("Snapshot has no data.");

      let batch = writeBatch(db);
      let batchCount = 0;
      let processed = 0;

      // Before rolling back, save a snapshot of current active state
      const activeSnapshot = await getDocs(
        query(collection(db, "students"), where("activeqr", "==", true)),
      );
      const currentData = activeSnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      saveRollbackSnapshot(`Pre-rollback safety (auto)`, currentData);

      for (const student of rows) {
        if (!student.id) continue;
        const { id, ...payload } = student;
        batch.update(doc(db, "students", id), {
          ...payload,
          updatedAt: serverTimestamp(),
        });
        batchCount++;
        processed++;
        if (batchCount >= BATCH_SIZE_LIMIT) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
      if (batchCount > 0) await batch.commit();

      setSnackbar({
        open: true,
        severity: "success",
        message: `Rolled back ${processed} student records.`,
      });
      setRollbackTarget(null);
      onRollbackComplete?.();
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: getReadableErrorMessage(error),
      });
    } finally {
      setRolling(false);
    }
  }, [rollbackTarget, onRollbackComplete, setSnackbar]);

  if (!history.length) {
    return (
      <Alert severity="info" icon={<HistoryRoundedIcon />}>
        No rollback snapshots yet. Snapshots are saved automatically before
        every bulk update.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Snapshots are saved automatically before every bulk update. You can
        restore any snapshot below. A safety snapshot of the current state is
        taken before rolling back.
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                Records
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((entry) => (
              <TableRow key={entry.id} hover>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {new Date(entry.timestamp).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>
                    {entry.label}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip label={entry.count} size="small" />
                </TableCell>
                <TableCell align="right">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    justifyContent="flex-end"
                  >
                    <Tooltip title="Download as Excel">
                      <IconButton
                        size="small"
                        onClick={() => downloadRollbackAsExcel(entry)}
                      >
                        <DownloadRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => setRollbackTarget(entry)}
                      sx={{ borderRadius: 999, fontSize: 12 }}
                    >
                      Restore
                    </Button>
                    <Tooltip title="Delete snapshot">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(entry.id)}
                      >
                        ×
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Rollback confirmation dialog */}
      <Dialog
        open={Boolean(rollbackTarget)}
        onClose={() => !rolling && setRollbackTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberRoundedIcon color="warning" />
          Confirm Rollback
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will overwrite <strong>{rollbackTarget?.count}</strong> student
            records with the snapshot from{" "}
            <strong>
              {rollbackTarget &&
                new Date(rollbackTarget.timestamp).toLocaleString()}
            </strong>
            .
            <br />
            <br />
            A safety snapshot of the current state will be saved before
            restoring.
            <br />
            <br />
            <em>"{rollbackTarget?.label}"</em>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRollbackTarget(null)}
            disabled={rolling}
            sx={{ borderRadius: 999 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRollback}
            disabled={rolling}
            startIcon={
              rolling ? <CircularProgress size={16} /> : <HistoryRoundedIcon />
            }
            sx={{ borderRadius: 999 }}
          >
            {rolling ? "Restoring..." : "Restore Snapshot"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ─────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────

export default function StudentExcelManager() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [user, setUser] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [previewLimit] = useState(20);

  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ total: 0, done: 0 });
  const [importSummary, setImportSummary] = useState(null);

  const [exporting, setExporting] = useState(false);
  const [exportEmbedImages, setExportEmbedImages] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedFields, setSelectedFields] = useState(
    new Set(ALL_UPDATABLE_FIELDS),
  );

  // Confirm dialog before bulk update
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // "assign" | "update"

  const [snackbarState, setSnackbarState] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const setSnackbar = useCallback((state) => setSnackbarState(state), []);

  // ── Auth ──────────────────────────────────
  useEffect(() => {
    let active = true;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!active) return;
      try {
        if (!currentUser) {
          setUser(null);
          setCanManage(false);
          return;
        }
        setUser(currentUser);
        const snap = await getDocs(
          query(
            collection(db, "users"),
            where("__name__", "==", currentUser.uid),
          ),
        );
        const data = snap.empty ? null : snap.docs[0].data();
        setCanManage(
          Boolean(data && hasPermission(data, PERMISSIONS.ADMIN_DASHBOARD)),
        );
      } finally {
        if (active) setAuthLoading(false);
      }
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const allowedRoles = useMemo(
    () => getAllowedRoles(PERMISSIONS.ADMIN_DASHBOARD).join(", "),
    [],
  );
  const previewRows = useMemo(
    () => parsedRows.slice(0, previewLimit),
    [parsedRows, previewLimit],
  );

  // ── Tab change ────────────────────────────
  const handleTabChange = useCallback((_, newValue) => {
    setCurrentTab(newValue);
    setSelectedFile(null);
    setParsedRows([]);
    setImportSummary(null);
    setImportProgress({ total: 0, done: 0 });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  // ── Parse file ────────────────────────────
  const handleParseFile = useCallback(async (file) => {
    setSelectedFile(file || null);
    setParsedRows([]);
    setImportSummary(null);
    if (!file) return;

    setParsing(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array", cellDates: true });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) throw new Error("Excel file has no worksheets.");

      const worksheet = workbook.Sheets[firstSheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
        raw: true,
      });

      const normalizedRows = rawRows
        .map((row) => normalizeRow(row))
        .filter((row) => !isBlankRow(row))
        .map((row) => ({
          ...row,
          className: normalizeClassName(row.className),
        }));

      setParsedRows(normalizedRows);
      setSnackbarState({
        open: true,
        severity: "success",
        message: `Parsed ${normalizedRows.length} student rows.`,
      });
    } catch (error) {
      console.error("Excel parse error:", error);
      setSnackbarState({
        open: true,
        severity: "error",
        message: getReadableErrorMessage(error),
      });
    } finally {
      setParsing(false);
    }
  }, []);

  // ── Validate rows before action ───────────
  const validateRowsForUpdate = useCallback(() => {
    const missingUid = parsedRows.filter((r) => !String(r.uid || "").trim());
    if (missingUid.length) {
      setSnackbarState({
        open: true,
        severity: "warning",
        message: `${missingUid.length} row(s) are missing a uid and will be skipped.`,
      });
    }
    const validRows = parsedRows.filter((r) => String(r.uid || "").trim());
    return validRows;
  }, [parsedRows]);

  // ── Import: Assign to Inactive QRs ────────
  const handleImport = useCallback(async () => {
    if (!parsedRows.length) {
      setSnackbarState({
        open: true,
        severity: "warning",
        message: "Please select an Excel file with at least 1 student row.",
      });
      return;
    }
    setImporting(true);
    setImportSummary(null);
    setImportProgress({ total: parsedRows.length, done: 0 });

    try {
      const inactiveSnapshot = await getDocs(
        query(collection(db, "students"), where("activeqr", "==", false)),
      );

      const inactiveQrPool = inactiveSnapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((s) => Boolean(String(s.qrImageUrl || "").trim()))
        .filter(
          (s) =>
            String(s.name || "").trim() === "" &&
            String(s.className || "").trim() === "" &&
            String(s.phone || "").trim() === "",
        )
        .sort((a, b) => String(a.uid || "").localeCompare(String(b.uid || "")));

      if (inactiveQrPool.length < parsedRows.length) {
        throw new Error(
          `Not enough inactive QR codes available. Needed ${parsedRows.length}, found ${inactiveQrPool.length}.`,
        );
      }

      // Save rollback snapshot of pool items before assigning
      const snapshotData = inactiveQrPool.slice(0, parsedRows.length);
      saveRollbackSnapshot(
        `Before assigning ${parsedRows.length} new students (inactive QR)`,
        snapshotData,
      );

      let poolIndex = 0;
      let processed = 0;
      let batch = writeBatch(db);
      let batchCount = 0;

      for (let i = 0; i < parsedRows.length; i++) {
        const row = parsedRows[i];
        const assignedQr = inactiveQrPool[poolIndex++];
        const uidValue = String(assignedQr.uid || "")
          .trim()
          .toUpperCase();

        const payload = {
          ...row,
          uid: uidValue,
          qrImageUrl: assignedQr.qrImageUrl || "",
          activeqr: shouldActivateQrForStudentName(row.name),
          updatedAt: serverTimestamp(),
        };

        batch.update(doc(db, "students", assignedQr.id), payload);
        batchCount++;
        processed++;

        if (batchCount >= BATCH_SIZE_LIMIT) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }

        setImportProgress({ total: parsedRows.length, done: processed });
      }

      if (batchCount > 0) await batch.commit();

      setImportSummary({ imported: processed, assignedQrs: processed });
      setSnackbarState({
        open: true,
        severity: "success",
        message: `Imported ${processed} students and assigned ${processed} inactive QR codes.`,
      });
    } catch (error) {
      console.error("Excel import error:", error);
      setSnackbarState({
        open: true,
        severity: "error",
        message: getReadableErrorMessage(error),
      });
    } finally {
      setImporting(false);
    }
  }, [parsedRows]);

  // ── Update by UID (any student, regardless of activeqr) ──
  const handleUpdateActiveQrs = useCallback(
    async (activeSelectedFields) => {
      const validRows = validateRowsForUpdate();
      if (!validRows.length) {
        setSnackbarState({
          open: true,
          severity: "warning",
          message: "No valid rows with a uid found.",
        });
        return;
      }

      setImporting(true);
      setImportSummary(null);
      setImportProgress({ total: validRows.length, done: 0 });

      try {
        // Fetch ALL students — no activeqr filter, match purely by uid
        const allSnap = await getDocs(collection(db, "students"));

        const uidMap = new Map(); // normalizedUid → docId
        const uidDataMap = new Map(); // normalizedUid → full doc data (for rollback)

        allSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const candidates = [data.uid, docSnap.id]
            .map((v) => normalizeStudentUid(v))
            .filter(Boolean);
          candidates.forEach((uid) => {
            if (!uidMap.has(uid)) {
              uidMap.set(uid, docSnap.id);
              uidDataMap.set(uid, { id: docSnap.id, ...data });
            }
          });
        });

        // Collect pre-update state for rollback
        const rollbackData = [];
        const matchedDocIds = new Set();

        validRows.forEach((row) => {
          const uid = normalizeStudentUid(row.uid);
          if (!uid) return;
          const docId = uidMap.get(uid);
          if (!docId || matchedDocIds.has(docId)) return;
          matchedDocIds.add(docId);
          const original = uidDataMap.get(uid);
          if (original) rollbackData.push(original);
        });

        if (rollbackData.length > 0) {
          saveRollbackSnapshot(
            `Before updating ${rollbackData.length} students (uid-only match)`,
            rollbackData,
          );
        }

        let batch = writeBatch(db);
        let batchCount = 0;
        let processed = 0;
        let notFoundCount = 0;
        let skippedCount = 0;
        const notFoundRows = [];

        for (let i = 0; i < validRows.length; i++) {
          const row = validRows[i];
          const uid = normalizeStudentUid(row.uid);

          if (!uid) {
            skippedCount++;
            continue;
          }

          const studentDocId = uidMap.get(uid);
          if (!studentDocId) {
            notFoundCount++;
            notFoundRows.push({
              uid: row.uid,
              name: String(row.name || ""),
              className: String(row.className || ""),
              phone: String(row.phone || ""),
            });
            continue;
          }

          // Build payload — only include fields the user selected (plus always-locked uid)
          const filteredRow = {};
          Object.entries(row).forEach(([key, value]) => {
            if (ALWAYS_LOCKED.has(key) || activeSelectedFields.has(key)) {
              filteredRow[key] = value;
            }
          });

          const payload = {
            ...filteredRow,
            uid,
            // Always force activeqr = true when updating by uid
            activeqr: true,
            updatedAt: serverTimestamp(),
          };

          batch.update(doc(db, "students", studentDocId), payload);
          batchCount++;
          processed++;

          if (batchCount >= BATCH_SIZE_LIMIT) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }

          setImportProgress({
            total: validRows.length,
            done: processed + notFoundCount + skippedCount,
          });
        }

        if (batchCount > 0) await batch.commit();

        setImportSummary({
          imported: processed,
          notFound: notFoundCount,
          skipped: skippedCount,
          notFoundRows,
        });
        setSnackbarState({
          open: true,
          severity: "success",
          message:
            `Updated ${processed} students (activeqr set to true).` +
            (notFoundCount > 0
              ? ` ${notFoundCount} UIDs not found in Firestore.`
              : "") +
            (skippedCount > 0 ? ` ${skippedCount} rows had no UID.` : ""),
        });
      } catch (error) {
        console.error("Excel update error:", error);
        setSnackbarState({
          open: true,
          severity: "error",
          message: getReadableErrorMessage(error),
        });
      } finally {
        setImporting(false);
      }
    },
    [validateRowsForUpdate],
  );

  // ── Confirm dialog gate ───────────────────
  const requestAction = useCallback(
    (action) => {
      if (!parsedRows.length) {
        setSnackbarState({
          open: true,
          severity: "warning",
          message: "Please select an Excel file with at least 1 student row.",
        });
        return;
      }
      setPendingAction(action);
      setConfirmDialogOpen(true);
    },
    [parsedRows],
  );

  const handleConfirm = useCallback(() => {
    setConfirmDialogOpen(false);
    if (pendingAction === "assign") handleImport();
    else if (pendingAction === "update") handleUpdateActiveQrs(selectedFields);
    setPendingAction(null);
  }, [pendingAction, handleImport, handleUpdateActiveQrs, selectedFields]);

  // ── Export ────────────────────────────────
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const snapshot = await getDocs(
        query(collection(db, "students"), where("activeqr", "==", true)),
      );

      const students = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (!students.length) {
        setSnackbarState({
          open: true,
          severity: "info",
          message: "No active-QR students found.",
        });
        return;
      }

      const extraHeaders = Array.from(
        students.reduce((acc, s) => {
          Object.keys(s || {}).forEach((k) => {
            if (!TEMPLATE_HEADERS.includes(k) && k !== "id") acc.add(k);
          });
          return acc;
        }, new Set()),
      ).sort((a, b) => a.localeCompare(b));

      const rows = students
        .sort((a, b) => String(a.uid || "").localeCompare(String(b.uid || "")))
        .map((s) => buildExportRow(s, extraHeaders));

      const workbook = await buildActiveQrWorkbook(
        rows,
        extraHeaders,
        exportEmbedImages,
      );
      const dateStamp = new Date().toISOString().slice(0, 10);
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `active_qr_students_${dateStamp}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);

      setSnackbarState({
        open: true,
        severity: "success",
        message: `Exported ${rows.length} active-QR students.`,
      });
    } catch (error) {
      console.error("Excel export error:", error);
      setSnackbarState({
        open: true,
        severity: "error",
        message: getReadableErrorMessage(error),
      });
    } finally {
      setExporting(false);
    }
  }, [exportEmbedImages]);

  // ── Preview table (reusable) ──────────────
  const PreviewTable = useMemo(() => {
    if (!parsedRows.length) return null;
    return (
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Uploaded details preview
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Showing first {Math.min(parsedRows.length, previewRows.length)} of{" "}
            {parsedRows.length} rows
          </Typography>
        </Stack>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 420 }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                {TEMPLATE_HEADERS.map((h) => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows.map((row, idx) => (
                <TableRow key={`${row.uid || "row"}-${idx}`} hover>
                  {TEMPLATE_HEADERS.map((h) => {
                    const val = buildPreviewValue(row[h]);
                    return (
                      <TableCell key={h} sx={{ whiteSpace: "nowrap" }}>
                        {val || <span style={{ opacity: 0.45 }}>empty</span>}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    );
  }, [parsedRows, previewRows]);

  // ── Loading / Auth gate ───────────────────
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 120px)",
          display: "grid",
          placeItems: "center",
          bgcolor: "grey.50",
          px: 2,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!user || !canManage) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 120px)",
          display: "grid",
          placeItems: "center",
          bgcolor: "grey.50",
          px: 2,
        }}
      >
        <Card
          sx={{
            maxWidth: 520,
            width: "100%",
            borderRadius: 4,
            boxShadow: "none",
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h5" fontWeight={800}>
              Access required
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Users with these roles can use the Excel manager:{" "}
              {allowedRoles || "admin"}.
            </Typography>
            <Button
              onClick={() => navigate("/login")}
              variant="contained"
              sx={{ mt: 3, borderRadius: 999 }}
            >
              Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Main UI ───────────────────────────────
  const progressPercent =
    importProgress.total > 0
      ? Math.round((importProgress.done / importProgress.total) * 100)
      : 0;

  return (
    <Box sx={{ py: 4, bgcolor: "grey.50", minHeight: "calc(100vh - 120px)" }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack spacing={0.5}>
              <Typography variant="h4" fontWeight={900}>
                Student Excel Manager
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Import, update, export student data. Rollback snapshots are
                saved automatically before every change.
              </Typography>
            </Stack>
            <Button
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate("/admin-dashboard")}
              sx={{ borderRadius: 999 }}
            >
              Back
            </Button>
          </Stack>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Assign to Inactive QRs" />
              <Tab label="Update by UID" />
              <Tab label="Export Data" />
              <Tab
                label="Rollback History"
                icon={<HistoryRoundedIcon fontSize="small" />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={(e) => handleParseFile(e.target.files?.[0] || null)}
          />

          {/* ── Tab 0: Assign to Inactive QRs ── */}
          {currentTab === 0 && (
            <Card sx={{ borderRadius: 4, boxShadow: "none" }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack spacing={2.5}>
                  <Typography variant="h6" fontWeight={800}>
                    Import for Inactive QRs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upload new student details. The system assigns them to
                    available inactive QR codes. A rollback snapshot is saved
                    automatically before writing.
                  </Typography>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems="center"
                  >
                    <Button
                      variant="outlined"
                      startIcon={
                        parsing ? (
                          <CircularProgress size={18} />
                        ) : (
                          <UploadFileRoundedIcon />
                        )
                      }
                      disabled={parsing || importing}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ borderRadius: 999 }}
                    >
                      {selectedFile ? "Change Excel file" : "Choose Excel file"}
                    </Button>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ flex: 1 }}
                    >
                      {selectedFile
                        ? `${selectedFile.name} · ${parsedRows.length} rows`
                        : "Upload the filled student Excel file (.xlsx). Missing cells stay empty in the database."}
                    </Typography>
                  </Stack>

                  {PreviewTable}

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems="center"
                  >
                    <Button
                      variant="contained"
                      disabled={importing || parsing || !parsedRows.length}
                      onClick={() => requestAction("assign")}
                      sx={{ borderRadius: 999 }}
                    >
                      {importing
                        ? "Importing..."
                        : "Import & Assign Inactive QR"}
                    </Button>
                    {importing && (
                      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 160 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progressPercent}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {importProgress.done}/{importProgress.total} processed
                          ({progressPercent}%)
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  {importSummary && (
                    <Alert
                      severity="success"
                      icon={<CheckCircleOutlineRoundedIcon />}
                    >
                      <AlertTitle>Done</AlertTitle>
                      Imported <strong>{importSummary.imported}</strong>{" "}
                      students. Assigned{" "}
                      <strong>{importSummary.assignedQrs}</strong> inactive QR
                      codes.
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* ── Tab 1: Update Active QRs ── */}
          {currentTab === 1 && (
            <Card sx={{ borderRadius: 4, boxShadow: "none" }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack spacing={2.5}>
                  <Typography variant="h6" fontWeight={800}>
                    Update Students by UID
                  </Typography>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Matches students by <strong>uid</strong> across <em>all</em>{" "}
                    records — no activeqr filter. Every matched student will
                    have <strong>activeqr set to true</strong> automatically.
                    Only the fields you check below will be written. A rollback
                    snapshot is saved before every write.
                  </Alert>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems="center"
                  >
                    <Button
                      variant="outlined"
                      startIcon={
                        parsing ? (
                          <CircularProgress size={18} />
                        ) : (
                          <UploadFileRoundedIcon />
                        )
                      }
                      disabled={parsing || importing}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ borderRadius: 999 }}
                    >
                      {selectedFile ? "Change Excel file" : "Choose Excel file"}
                    </Button>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ flex: 1 }}
                    >
                      {selectedFile
                        ? `${selectedFile.name} · ${parsedRows.length} rows`
                        : "Upload the updated active students Excel file (.xlsx)."}
                    </Typography>
                  </Stack>

                  {PreviewTable}

                  <FieldSelector
                    selectedFields={selectedFields}
                    onChange={setSelectedFields}
                  />

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems="center"
                  >
                    <Button
                      variant="contained"
                      disabled={
                        importing ||
                        parsing ||
                        !parsedRows.length ||
                        selectedFields.size === 0
                      }
                      onClick={() => requestAction("update")}
                      sx={{ borderRadius: 999 }}
                    >
                      {importing ? "Updating..." : "Update by UID"}
                    </Button>
                    {importing && (
                      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 160 }}>
                        <LinearProgress
                          variant="determinate"
                          value={progressPercent}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {importProgress.done}/{importProgress.total} processed
                          ({progressPercent}%)
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  {importSummary && (
                    <Stack spacing={1.5}>
                      <Alert
                        severity={
                          importSummary.notFound > 0 ? "warning" : "success"
                        }
                        icon={
                          importSummary.notFound > 0 ? (
                            <WarningAmberRoundedIcon />
                          ) : (
                            <CheckCircleOutlineRoundedIcon />
                          )
                        }
                      >
                        <AlertTitle>
                          {importSummary.notFound > 0
                            ? "Completed with unmatched rows"
                            : "Done"}
                        </AlertTitle>
                        Updated <strong>{importSummary.imported}</strong>{" "}
                        students.
                        {importSummary.notFound > 0 && (
                          <>
                            {" "}
                            <strong>{importSummary.notFound}</strong> rows
                            didn't match any active QR UID — see table below.
                          </>
                        )}
                        {importSummary.skipped > 0 && (
                          <>
                            {" "}
                            <strong>{importSummary.skipped}</strong> rows had no
                            UID and were skipped.
                          </>
                        )}
                      </Alert>

                      {importSummary.notFoundRows?.length > 0 && (
                        <Card
                          variant="outlined"
                          sx={{ borderRadius: 3, borderColor: "warning.main" }}
                        >
                          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mb: 1.5 }}
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <WarningAmberRoundedIcon
                                  color="warning"
                                  fontSize="small"
                                />
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={700}
                                >
                                  Unmatched UIDs (
                                  {importSummary.notFoundRows.length})
                                </Typography>
                              </Stack>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<DownloadRoundedIcon />}
                                sx={{ borderRadius: 999, fontSize: 12 }}
                                onClick={() => {
                                  const sheet = XLSX.utils.json_to_sheet(
                                    importSummary.notFoundRows,
                                  );
                                  const wb = XLSX.utils.book_new();
                                  XLSX.utils.book_append_sheet(
                                    wb,
                                    sheet,
                                    "Unmatched_UIDs",
                                  );
                                  XLSX.writeFile(
                                    wb,
                                    `unmatched_uids_${new Date().toISOString().slice(0, 10)}.xlsx`,
                                  );
                                }}
                              >
                                Download Excel
                              </Button>
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mb: 1, display: "block" }}
                            >
                              These UIDs from your Excel file were not found
                              anywhere in Firestore. Check for typos, or use the
                              "Assign to Inactive QRs" tab if they are brand-new
                              students.
                            </Typography>
                            <TableContainer sx={{ maxHeight: 300 }}>
                              <Table stickyHeader size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                      #
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                      UID (from Excel)
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                      Name
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                      Class
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                      Phone
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {importSummary.notFoundRows.map(
                                    (row, idx) => (
                                      <TableRow
                                        key={`nf-${idx}`}
                                        sx={{ bgcolor: "warning.50" }}
                                      >
                                        <TableCell
                                          sx={{ color: "text.secondary" }}
                                        >
                                          {idx + 1}
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label={row.uid || "—"}
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                            sx={{
                                              fontFamily: "monospace",
                                              fontWeight: 700,
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          {row.name || (
                                            <span style={{ opacity: 0.4 }}>
                                              empty
                                            </span>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {row.className || (
                                            <span style={{ opacity: 0.4 }}>
                                              empty
                                            </span>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {row.phone || (
                                            <span style={{ opacity: 0.4 }}>
                                              empty
                                            </span>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ),
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </CardContent>
                        </Card>
                      )}
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* ── Tab 2: Export ── */}
          {currentTab === 2 && (
            <Card sx={{ borderRadius: 4, boxShadow: "none" }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack spacing={2.5}>
                  <Typography variant="h6" fontWeight={800}>
                    Export active QR students
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Downloads an Excel file with details from Firestore for
                    students where <strong>activeqr = true</strong>.
                  </Typography>
                  <Divider />
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems="center"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={exportEmbedImages}
                          onChange={(e) =>
                            setExportEmbedImages(Boolean(e.target.checked))
                          }
                        />
                      }
                      label={
                        exportEmbedImages
                          ? "Embed images in Excel (may increase file size)"
                          : "Export image URLs only"
                      }
                    />

                    <Button
                      variant="outlined"
                      startIcon={
                        exporting ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DownloadRoundedIcon />
                        )
                      }
                      disabled={exporting || importing}
                      onClick={handleExport}
                      sx={{ borderRadius: 999 }}
                    >
                      {exporting ? "Exporting..." : "Export Active QR Excel"}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* ── Tab 3: Rollback History ── */}
          {currentTab === 3 && (
            <Card sx={{ borderRadius: 4, boxShadow: "none" }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack spacing={2.5}>
                  <Typography variant="h6" fontWeight={800}>
                    Rollback History
                  </Typography>
                  <RollbackPanel
                    setSnackbar={setSnackbar}
                    onRollbackComplete={() => {
                      setSnackbarState({
                        open: true,
                        severity: "info",
                        message:
                          "Rollback complete. Verify the data in Firestore.",
                      });
                    }}
                  />
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Container>

      {/* ── Confirm action dialog ── */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !importing && setConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {pendingAction === "assign"
            ? "Confirm Import & Assign"
            : "Confirm Bulk Update"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingAction === "assign"
              ? `This will assign ${parsedRows.length} rows to inactive QR codes in Firestore. A rollback snapshot will be saved before writing.`
              : `This will update up to ${parsedRows.length} active students in Firestore. Only ${selectedFields.size} selected field(s) will be written per record. A rollback snapshot will be saved before writing.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            sx={{ borderRadius: 999 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            sx={{ borderRadius: 999 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={6000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setSnackbarState((s) => ({ ...s, open: false }));
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbarState.severity} variant="filled">
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
